import { useCallback } from 'react';
import CryptoJS from 'crypto-js';

// WASM 래퍼 임포트
import {
  ensureWasm,
  overwrite_wallet_data,
  store_encrypted_data,
  store_network_encrypted_data,
  retrieve_encrypted_data,
  retrieve_network_encrypted_data,
  check_pincode_match,
  change_password_wasm,
  set_wallet_address_wasm,
  get_wallet_address_wasm,
  set_network_wallet_address_wasm,
  get_network_wallet_address_wasm,
  storage_set,
  storage_get,
  storage_clear,
} from '../wasm/secureStorage';

// Chrome Storage helper functions
const setChromeStorage = (data: { [key: string]: any }): Promise<void> => {
    return new Promise((resolve, reject) => {
        chrome.storage.local.set(data, () => {
            if (chrome.runtime.lastError) {
                reject(new Error(chrome.runtime.lastError.message)); // lastError를 Error 객체로 변환
            } else {
                resolve(); // 데이터가 성공적으로 저장된 경우
            }
        });
    });
};

const getChromeStorage = async (keys: string[]) => {
    return new Promise<any>((resolve, reject) => {
        chrome.storage.local.get(keys, (result) => {
            if (chrome.runtime.lastError) {
                return reject(chrome.runtime.lastError);
            }
            resolve(result);
        });
    });
};

const getChromeStorageSingle = async (key: string): Promise<any> => {
	return new Promise((resolve, reject) => {
			chrome.storage.local.get(key, (result) => {
					if (chrome.runtime.lastError) {
							reject(new Error(chrome.runtime.lastError.message));
					} else {
							resolve(result[key]);
					}
			});
	});
};

const clearChromeStorage = (): Promise<void> => {
	return new Promise((resolve, reject) => {
			chrome.storage.local.clear(() => {
					if (chrome.runtime.lastError) {
							reject(new Error(chrome.runtime.lastError.message));
					} else {
							resolve();
					}
			});
	});
};

interface EncryptedType {
  encrypted: string;
  salt: string;
  iv: string;
}

// sha256 hash 생성
const hashSha256Value = async (data: string): Promise<string> => {
	try {
		const hash = CryptoJS.SHA256(data).toString();
		return hash
	} catch (error) {
		console.warn(`Error hashSha256Value`, error);
	}

	return ''
};

const useSecureStorage = () => {

    //----------------------------------------------------------------------//
    // AES 관련 로직은 Wasm(`secure_storage.rs`)로 이관됨
    // - TS에서 직접 암복호화하지 않고 Wasm 메서드를 호출하여 포맷을 단일화
    //----------------------------------------------------------------------//

	//----------------------------------------------------------------------//
    // 3DES
	// PBKDF2 키 유도 함수 (동기적으로 동작)
	// const deriveKey = (password: string, salt: string): CryptoJS.lib.WordArray => {
	// 	// PBKDF2 with 10000 iterations and 192-bit key (3DES는 최대 192-bit 키 사용)
	// 	const key = CryptoJS.PBKDF2(password, CryptoJS.enc.Hex.parse(salt), {
	// 		keySize: 192 / 32, // 192-bit key
	// 		iterations: 10000,
	// 		hasher: CryptoJS.algo.SHA256
	// 	});
	// 	return key;
	// };

	// // 데이터 암호화 함수
	// const encryptValue = (password: string, data: string): { encrypted: string, salt: string, iv: string } => {
	// 	try {
	// 		// 랜덤한 salt 생성 (16바이트)
	// 		const salt = CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex);

	// 		// PBKDF2를 사용하여 키 유도
	// 		const key = deriveKey(password, salt);

	// 		// 랜덤한 IV 생성 (8바이트, 3DES는 64-bit 블록 크기)
	// 		const iv = CryptoJS.lib.WordArray.random(8).toString(CryptoJS.enc.Hex);

	// 		// 3DES-CBC 모드를 사용하여 데이터 암호화
	// 		const encrypted = CryptoJS.TripleDES.encrypt(data, key, {
	// 			iv: CryptoJS.enc.Hex.parse(iv),
	// 			mode: CryptoJS.mode.CBC,
	// 			padding: CryptoJS.pad.Pkcs7
	// 		}).toString();

	// 		// 암호화된 데이터와 함께 salt, iv 반환
	// 		return {
	// 			encrypted,
	// 			salt,
	// 			iv
	// 		};
	// 	} catch (error) {
	// 		console.error(`Error encryptValue`, error);
	// 	}

	// 	return { encrypted: '', salt: '', iv: '' };
	// };

	// // 데이터 복호화 함수
	// const decryptValue = (password: string, encryptedData: { encrypted: string, salt: string, iv: string }): string => {
	// 	try {
	// 		const { encrypted, salt, iv } = encryptedData;

	// 		// PBKDF2를 사용하여 키 유도
	// 		const key = deriveKey(password, salt);

	// 		// 3DES-CBC 모드를 사용하여 데이터 복호화
	// 		const decrypted = CryptoJS.TripleDES.decrypt(encrypted, key, {
	// 			iv: CryptoJS.enc.Hex.parse(iv),
	// 			mode: CryptoJS.mode.CBC,
	// 			padding: CryptoJS.pad.Pkcs7
	// 		});

	// 		// 복호화된 데이터를 UTF-8 문자열로 변환
	// 		const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
	// 		return decryptedText;
	// 	} catch (error) {
	// 		console.error(`Error decryptValue`, error);
	// 	}

	// 	return '';
	// };
	//----------------------------------------------------------------------//

	/**
	 * 로컬스토리지 키 검색 삭제
	 * @param substring 
	 */
	const deleteKeysContainingLocalStorage = (substring:string) => {
		for (let i = 0; i < localStorage.length; i++) {
		  const key = localStorage.key(i); // 현재 키 가져오기
			if (key && key.includes(substring)) {
				localStorage.removeItem(key); // 해당 키 삭제
				i--; // 키 삭제 후 인덱스 보정
			}
		}
	};

	/**
	 * 지정한 네트워크 니모닉, 비밀키, 퍼블릭키 암호화 저장
	 * 
	 * @param network
	 * @param privateKey
	 * @param publicKey
	 * @param mnemonic
	 * 
	 * @return boolean
	 */
    const storeNetworkEncryptedData = 
    useCallback(async (
        network: string,
        privateKey: string,
        publicKey: string,
        mnemonic: string | string[],
        pin: string
    ) => {
        try {
            if (!network || network === '') {
                console.error('Network cannot be null or empty');
                return false;
            }

            // 한국어 주석: Wasm 메서드로 암복호화 및 저장을 일괄 수행
            await ensureWasm();
            const mnemonicString = Array.isArray(mnemonic) ? mnemonic.join(' ') : mnemonic;
            const ok = await store_network_encrypted_data(network, privateKey, publicKey, mnemonicString, pin);
            return !!ok;
        } catch (error) {
            console.error('fail storeEncryptedData', error);
            return false;
        }
    },[]);
	
	/**
	 * 지갑에 활성화 된 니모닉, 비밀키, 퍼블릭키 암호화 저장
	 * 
	 * @param walletAddress
	 * @param privateKey
	 * @param publicKey
	 * @param mnemonic
	 * 
	 * @return boolean
	 */
    const storeEncryptedData = useCallback(
        async (privateKey: string, publicKey: string, mnemonic: string | string[], pin: string) => {
        try {
            await ensureWasm();
            const mnemonicString = Array.isArray(mnemonic) ? mnemonic.join(' ') : mnemonic;
            const ok = await store_encrypted_data(privateKey, publicKey, mnemonicString, pin);
            return !!ok;
        } catch (error) {
            console.error('fail storeEncryptedData', error);
            return false;
        }
    }, []);

	/**
	 * 지정한 네트워크의 지갑정보가 로컬스토리지에 있는지 확인
	 * 
	 * @param network
	 * 
	 * @return boolean
	 */
	const checkSavedNetworkWalletData = useCallback(async (network: string) => {
		try {
			if (!network || network === '') {
				console.error('Network cannot be null or empty');
				return false;
			}

			// 한국어 주석: Wasm 스토리지 기반으로 4가지(비밀키/퍼블릭키/니모닉/지갑주소) 모두 확인
			await ensureWasm();
			const networkLower = network.toLowerCase();

			const tagPrivateKey = `encryptedPrivateKey_${networkLower}`;
			const tagPublicKey = `encryptedPublicKey_${networkLower}`;
			const tagMnemonic = `encryptedMnemonic_${networkLower}`;
			const legacyAddrKey = `walletAddress_${networkLower}`;

			// 한국어 주석: 읽기 전용 확인. Wasm에서 없으면 레거시 localStorage를 읽되, 쓰기/마이그레이션은 수행하지 않음
			const fetchReadonly = async (key: string): Promise<string | undefined> => {
				const v = await storage_get(key);
				if (v) return v;
				return localStorage.getItem(key) ?? undefined;
			};

			const [encryptedPrivateKey, encryptedPublicKey, encryptedMnemonic] = await Promise.all([
				fetchReadonly(tagPrivateKey),
				fetchReadonly(tagPublicKey),
				fetchReadonly(tagMnemonic),
			]);

			// 주소는 공식 Wasm API 우선. 없으면 레거시 localStorage 값을 읽기만 함(쓰기 없음)
			let walletAddress = get_network_wallet_address_wasm(networkLower);
			if (!walletAddress) {
				walletAddress = localStorage.getItem(legacyAddrKey) ?? undefined;
			}

			if (!encryptedPrivateKey || !encryptedPublicKey || !encryptedMnemonic || !walletAddress) {
				return false;
			}

			return true;
		} catch (error) {
			console.error('fail checkNetworkWalletData', error);
		}
		return false;
	}, []);

	/**
	 * 암호화된 지갑정보 덮어씌우기

	 * 
	 * @param network
	 * 
	 * @return boolean
	 */
    const overwriteWalletData = useCallback(async (network: string) => {
        try {
            await ensureWasm();
            const ok = await overwrite_wallet_data(network);
            if (!ok) {
                console.error('Error overwriteWalletData no encrypted wallet data');
            }
            return !!ok;
        } catch (error) {
            console.error('fail overwriteWalletData', error);
            return false;
        }
    }, []);

	/**
	 * 지정한 네트워크의 니모닉, 비밀키, 퍼블릭키 복호화
	 * 
	 * @param network
	 * @param pin
	 * 
	 * @return {privateKey, publicKey, mnemonic}
	 */
    const retrieveNetworkEncryptedData = 
    useCallback(async (network: string, pin: string) => {
        try {
            await ensureWasm();
            const json = await retrieve_network_encrypted_data(network, pin) as unknown as string;
            return JSON.parse(json);
        } catch (error) {
            console.error('fail retrieveNetworkEncryptedData', error);
            throw new Error('Failed to retrieve encrypted data');
        }
    }, []);

	/**
	 * 현재 활성화된 니모닉, 비밀키, 퍼블릭키 복호화
	 * 
	 * @param walletAddress
	 * @param pin
	 * 
	 * @return {privateKey, publicKey, mnemonic}
	 */
    const retrieveEncryptedData = useCallback(async (_walletAddress: string, pin: string) => {
        try {
            await ensureWasm();
            const json = await retrieve_encrypted_data(pin) as unknown as string;
            return JSON.parse(json);
        } catch (error) {
            console.error('fail retrieveEncryptedData', error);
            throw new Error('Failed to retrieve encrypted data');
        }
    }, []);

	/**
	 * 암호화 raw 데이터 요청
	 * 
	 * @return {privateKeyData, publicKeyData, mnemonicKeyData}
	 */
	// const retrieveRawEncryptedData = useCallback(async () => {
	// 		let privateKeyData
	// 		let publicKeyData
	// 		let mnemonicKeyData
			
	// 		privateKeyData = localStorage.getItem('encryptedPrivateKey')
	// 		publicKeyData = localStorage.getItem('encryptedPublicKey')
	// 		mnemonicKeyData = localStorage.getItem('encryptedMnemonic')

	// 		return { privateKeyData, publicKeyData, mnemonicKeyData };
    // }, []);

	/**
	 * 핀코드 확인
	 * 
	 * @param pin
	 * @param network
	 * 
	 * @return boolean
	 */

    const checkPincodeMatch = useCallback(async (pin: string, _network: string) => {
        try {
            await ensureWasm();
            const ok = await check_pincode_match(pin);
            return !!ok;
        } catch (error) {
            console.error('fail checkPincodeMatch', error);
            return false;
        }
    }, []);

    // 비밀번호 변경 함수
    const changePassword = useCallback(async (_walletAddress: string, currentPin: string, newPin: string) => {
        try {
            await ensureWasm();
            const ok = await change_password_wasm(currentPin, newPin);
            return !!ok;
        } catch (error) {
            console.error('fail changePassword', error);
            return false;
        }
    }, []);

	/**
	 * 지정한 네트워크의 지갑 주소 저장
	 * 
	 * @param walletAddress
	 * @param networkName
	 * 
	 * @return boolean
	 */
    const setNetworkWalletAddress = useCallback(async (walletAddress: string, networkName: string) => {
        try {
            await ensureWasm();
            const ok = await set_network_wallet_address_wasm(walletAddress, networkName);
            return !!ok;
        } catch (error) {
            console.error('fail setNetworkWalletAddress', error);
            return false;
        }
    }, []);

	/**
	 * 지정한 네트워크의 지갑 주소 리턴	
	 * 
	 * @param networkName
	 * 
	 * @return string
	 */
    const getNetworkWalletAddress = useCallback(async (network:string) => {
        try {
            await ensureWasm();
            const v = await get_network_wallet_address_wasm(network);
            return v ?? '';
        } catch (error) {
            console.error('fail getNetworkWalletAddress', error);
            return '';
        }
    }, []);

	/**
	 * 현재 활성화된 지갑 주소 저장
	 * 
	 * @param walletAddress
	 * 
	 * @return boolean
	 */
    const setWalletAddress = useCallback(async (walletAddress:string) => {
        try {
            await ensureWasm();
            const ok = await set_wallet_address_wasm(walletAddress);
            return !!ok;
        } catch (error) {
            console.error('fail setWalletAddress', error);
            return false;
        }
    }, []);

	/**
	 * 현재 지갑 주소 리턴	
	 * 
	 * @param networkName
	 * 
	 * @return string
	 */
    const getWalletAddress = useCallback(async () => {
        try {
            await ensureWasm();
            const v = await get_wallet_address_wasm();
            return v ?? '';
        } catch (error) {
            console.error('fail getWalletAddress', error);
            return '';
        }
    }, []);

	// 지갑생성 완료 상태 설정
    const setLogin = useCallback(async () => {
        await ensureWasm();
        await storage_set('register', 'true');
    }, []);

	// 지갑생성 완료 상태 해지
    const setLogout = useCallback(async () => {
        await ensureWasm();
        await storage_set('register', 'false');
        // 기존 캐시 키 삭제는 유지
        await deleteKeysContainingLocalStorage('TRANSACTION_DATA')
    }, []);

	// 로그인 상태 확인
    const getLogin = useCallback(async () => {
        await ensureWasm();
        const v = await storage_get('register');
        return v === 'true';
    }, []);

	// 코인/토큰 전송 내역 저장
    const setCacheLog = useCallback(async (cacheKey:string, data:string) => {
        await ensureWasm();
        await storage_set(cacheKey, data);
    }, []);

	// 코인/토큰 전송 내역 불러오기
    const getCacheLog = useCallback(async (cacheKey:string) => {
        await ensureWasm();
        return await storage_get(cacheKey);
    }, []);

	// nft 캐시 저장하기
    const setNfts = useCallback(async (nftKey:string, data:string) => {
        await ensureWasm();
        await storage_set(nftKey, data);
    }, []);

	// nft 캐시 불러오기
    const getNfts = useCallback(async (nftKey:string) => {
        await ensureWasm();
        return await storage_get(nftKey);
    }, []);

	// 자동 잠금 설정 저장
    const setAutoLockValue = useCallback(async (lockSetting:string) => {
        try {
            await ensureWasm();
            if (lockSetting != null && 1 < lockSetting.length) {
                const value = lockSetting.trim();
                await storage_set('autoLockSetting', value);
                return true;
            }
        } catch (error) {
            console.error('fail autoLockSetting', error);
        }
        return false;
    }, []);

	// 저장된 자동 잠금 설정 리턴
    // 자동 잠금 설정값 반환 (undefined를 null로 정규화)
    const getAutoLockValue = useCallback(async (): Promise<string | null> => {
        try {
            await ensureWasm();
            const v = await storage_get('autoLockSetting');
            return v ?? null;
        } catch (error) {
            console.error('fail getAutoLockSetting', error);
            return null;
        }
    }, []);
	
	// 지문인식 설정 저장
    const setFingerprintEnabled = useCallback(async (useSetting:string) => {
        try {
            await ensureWasm();
            if (useSetting != null && 1 < useSetting.length) {
                const value = useSetting.trim();
                await storage_set('fingerprintEnabled', value);
                return true;
            }
        } catch (error) {
            console.error('fail fingerprintEnabled', error);
        }
        return false;
    }, []);

	// 저장된 지문인식 설정 리턴
    const getFingerprintEnabled = useCallback(async () => {
        try {
            await ensureWasm();
            return await storage_get('fingerprintEnabled');
        } catch (error) {
            console.error('fail getFingerprintEnabled', error);
            return null;
        }
    }, []);

	// qSS 사용 상태 설정
    const setIsUseQss = useCallback(async (useSetting:string) => {
        try {
            await ensureWasm();
            if (useSetting != null && 1 < useSetting.length) {
                const value = useSetting.trim();
                await storage_set('isUseQss', value === 'true' ? 'true' : 'false');
                return true;
            }
        } catch (error) {
            console.error('fail isUseQss', error);
        }
        return false;
    }, []);

	// QSS 사용 상태
    const getIsUseQss = useCallback(async () => {
        try {
            await ensureWasm();
            const v = await storage_get('isUseQss');
            return v === 'true';
        } catch (error) {
            console.error('fail getIsUseQss', error);
            return false;
        }
    }, []);

	// qSS에 니모닉 저장 상태 설정
    const setIsSavedQssMnemonic = useCallback(async (useSetting:string) => {
        try {
            await ensureWasm();
            if (useSetting != null && 1 < useSetting.length) {
                const value = useSetting.trim();
                await storage_set('isSavedQssMnemonic', value === 'true' ? 'true' : 'false');
                return true;
            }
        } catch (error) {
            console.error('fail isUseQss', error);
        }
        return false;
    }, []);

	// qSS에 니모닉 저장 상태
    const getIsSavedQssMnemonic = useCallback(async () => {
        try {
            await ensureWasm();
            const v = await storage_get('isSavedQssMnemonic');
            return v === 'true';
        } catch (error) {
            console.error('fail isSavedQssMnemonic', error);
            return false;
        }
    }, []);

	// 현재 지갑의 니모식해시 저장
    const setLocalMnemonicHash = useCallback(async (mnemoinc:string) => {
        try {
            await ensureWasm();
            if (mnemoinc != null && 1 < mnemoinc.length) {
                const value = mnemoinc.trim();
                const hash = await hashSha256Value(value)
                await storage_set('mnemonicHash', hash);
                return true;
            }
        } catch (error) {
            console.error('fail isUseQss', error);
        }
        return false;
    }, []);

	// 저장된 현재지갑 니모닉해시 리턴
    const getLocalMnemonicHash = useCallback(async () => {
        try {
            await ensureWasm();
            const v = await storage_get('mnemonicHash');
            return v ?? '';
        } catch (error) {
            console.error('fail isSavedQssMnemonic', error);
            return '';
        }
    }, []);

	// 현재 선택된 블록체인 네트워크 저장
    const setBlockChain = useCallback(async (blockChain:string) => {
        try {
            await ensureWasm();
            if (blockChain != null && 1 < blockChain.length) {
                const value = blockChain.trim();
                await storage_set('blockChain', value);
                return true;
            }
        } catch (error) {
            console.error('fail setBlockChain', error);
        }
        return false;
    }, []);

    const getBlockChain = useCallback(async () => {
        try {
            await ensureWasm();
            const blockChain = await storage_get('blockChain');
            if (blockChain) {
                return blockChain.toLowerCase();
            } else {
                // 저장된 값이 없을 경우 env의 기본값 설정
                if(process.env.REACT_APP_NETWORK_NAME) {
                    await setBlockChain(process.env.REACT_APP_NETWORK_NAME!)
                    return process.env.REACT_APP_NETWORK_NAME;
                } else {
                    return ''
                }
            }
        } catch (error) {
            console.error('fail getBlockChain', error);
            return '';
        }
    }, []);

	/**
	 * 현재 네트워크의 메인 currency 설정
	 */
    const setNetworkCurrency = useCallback(async (currency:string) => {
        try {
            await ensureWasm();
            if (currency != null && 1 < currency.length) {
                const value = currency.trim();
                await storage_set('networkCurrency', value);
                return true;
            }
        } catch (error) {
            console.error('fail networkCurrency', error);
        }
        return false;
    }, []);

	/**
	 * 현재 설정된 currency 리턴
	 */
    const getNetworkCurrency = useCallback(async () => {
        try {
            await ensureWasm();
            const currency = await storage_get('networkCurrency');
            if (currency) {
                return currency.toLowerCase();
            } else {
                await setNetworkCurrency(process.env.REACT_APP_NETWORK_CURRENCY!.toLowerCase())
                return process.env.REACT_APP_NETWORK_CURRENCY!.toLowerCase()
            }
        } catch (error) {
            console.error('fail currency', error);
            return '';
        }
    }, []);

	/**
	 * 토큰 숨김 설정 (토글)
	 * 
	 * 숨김 처리 요청 시 지정한 네트워크의 토큰 목록에 해당 토큰을 추가하고,
	 * 이미 숨김 처리되어 있다면 목록에서 삭제합니다.
	 *
	 * @param token   - 숨김/표시 처리할 토큰 (예: { id: 'token1', name: '토큰명', ... })
	 * @param network - 네트워크 이름 (예: "solana", "bsc")
	 * 
	 * @return boolean - 처리 성공 여부 (true: 성공, false: 실패)
	 */
	const setHideToken = useCallback(async (token: any, network: string) => {
		try {
			if (!token || !network) {
				console.error('token 또는 network 값이 null 또는 공백입니다.');
				return false;
			}

			// 한국어 주석: Wasm 스토리지를 사용하여 hiddenTokens를 저장/갱신
			await ensureWasm();

			// 네트워크별 숨김 토큰 목록 키
			const storageKey = 'hiddenTokens';

			// 기존 저장값을 Wasm 스토리지에서 조회 (마이그레이션 없음)
			const storedValue = await storage_get(storageKey);

			let hiddenTokensObj: Record<string, string[]> = {};
			if (storedValue) {
				try {
					hiddenTokensObj = JSON.parse(storedValue);
				} catch (error) {
					console.error('hiddenTokens JSON 파싱 오류:', error);
					hiddenTokensObj = {};
				}
			}

			// 해당 네트워크에 대한 숨김 토큰 배열을 가져오거나, 없으면 빈 배열로 초기화합니다.
			let networkHiddenTokens = hiddenTokensObj[network] || [];
		
			// token이 객체라면 token.name, 아니라면 token 자체를 사용 (예: "tbep20")
			const tokenName = typeof token === 'object' ? token.name : token;
		
			// 이미 숨김 목록에 tokenName이 있는지 확인
			const existingIndex = networkHiddenTokens.findIndex((t) => t === tokenName);
		
			if (existingIndex >= 0) {
				// 이미 숨김 처리된 토큰이면 -> 배열에서 제거 (표시 처리)
				networkHiddenTokens.splice(existingIndex, 1);
			} else {
				// 숨김 처리되지 않은 토큰이면 -> 배열에 추가 (숨김 처리)
				networkHiddenTokens.push(tokenName);
			}
		
			// 업데이트된 네트워크별 숨김 토큰 배열을 객체에 저장하고 Wasm 스토리지에 반영
			hiddenTokensObj[network] = networkHiddenTokens;
			await storage_set(storageKey, JSON.stringify(hiddenTokensObj));
		
			return true;
		} catch (error) {
			console.error('fail setHideToken', error);
			return false;
		}
	}, []);

	/**
	 * 지정한 네트워크의 토큰 숨김처리 배열을 리턴
	 */
	const getHideToken = useCallback(async (network: string) => {
		try {
			if (!network || network.trim() === '') {
				console.error('geHideToken - 유효하지 않은 network 값:', network);
				return [];
			}

			await ensureWasm();
			const storageKey = 'hiddenTokens';

			// Wasm 스토리지에서만 조회 (마이그레이션 없음)
			const storedValue = await storage_get(storageKey);

			if (!storedValue) {
				return [];
			}

			// JSON 파싱하여 네트워크별 숨김 토큰 객체로 변환
			const hiddenTokensObj: Record<string, any[]> = JSON.parse(storedValue);
			// 지정한 네트워크의 토큰 배열을 가져오거나, 없으면 빈 배열
			const networkHiddenTokens = hiddenTokensObj[network] || [];
			
			return networkHiddenTokens;
		} catch (error) {
			console.error('fail geHideToken', error);
			return [];
		}
	}, []);

	// 스토리지 저장된 데이터 모두 clear
    const logOut = useCallback(async () => {
        await ensureWasm();
        await storage_clear();
    }, []);

	return {
		storeEncryptedData,
		storeNetworkEncryptedData,
		retrieveEncryptedData,
		retrieveNetworkEncryptedData,
		
		// retrieveRawEncryptedData,

		checkPincodeMatch,
		changePassword,
		checkSavedNetworkWalletData,

		setWalletAddress,
		setNetworkWalletAddress,
		getWalletAddress,
		getNetworkWalletAddress,

		overwriteWalletData,

		setLogin,
		setLogout,
		getLogin,
		setCacheLog,
		getCacheLog,
		setNfts,
		getNfts,
		setAutoLockValue,
		getAutoLockValue,
		setFingerprintEnabled,
		getFingerprintEnabled,
		setIsUseQss,
		getIsUseQss,
		setIsSavedQssMnemonic,
		getIsSavedQssMnemonic,
		setLocalMnemonicHash,
		getLocalMnemonicHash,
		setBlockChain,
		getBlockChain,
		setNetworkCurrency,
		getNetworkCurrency,
		setHideToken,
		getHideToken,
		logOut

	};
}

export default useSecureStorage;
